<script setup lang="ts">
import PartnersSelector from '@/components/Selector/PartnersSelector.vue'
import { type EventCreateModel } from '@/types/models/EventCreateModel'
import { EPartnersPerm } from '@/types/permissions/PartnersPermissionsEnum'
import {
    DateTimePickerField,
    InformationBaseCard,
    PermissionWrapper,
    StackLayout,
    StringInputField
} from '@rfc/rfc-components-library'
import { EProviders, type DateProviderType } from '@rfc/rfc-components-library/types'
import moment from 'moment'
import { inject } from 'vue'
import TimezoneSelector from '../components/TimezoneSelector.vue'

const { formatDate } = inject<DateProviderType>(EProviders.DATE, {})

type Props = {
    data: EventCreateModel
    submitted?: boolean
    hasErrors: {
        nameError: boolean
        startDateError: boolean
        endDateError: boolean
    }
}
const props = defineProps<Props>()

const emit = defineEmits<{
    (e: 'update', value: Partial<EventCreateModel>): void
}>()

const handleChange = (value: Partial<EventCreateModel>) => emit('update', value)

const handleChangeStartDate = (value: Date) =>
    emit('update', {
        ...props.data,
        start_at: value,
        end_at: moment(value).isAfter(moment(props.data.end_at).utc())
            ? moment(value).utc().toDate()
            : props.data.end_at
    })
</script>

<template>
    <InformationBaseCard :label="$t('event.createEvent')" class="input-information-card">
        <StackLayout direction="column" :gap="16" class="w-full">
            <StackLayout direction="row" :gap="8" class="w-full">
                <StackLayout direction="column" :gap="12" class="w-full">
                    <StringInputField
                        :label="$t('event.model.name')"
                        :modelValue="data.name"
                        @change="handleChange({ name: $event })"
                        :placeholder="$t('event.model.name')"
                        required
                        :hasError="hasErrors.nameError"
                        class="w-full" />
                    <StringInputField
                        :label="$t('event.model.comment')"
                        :modelValue="data.comments"
                        @change="handleChange({ comments: $event })"
                        :placeholder="$t('event.model.comment')"
                        inputType="textarea"
                        class="w-full" />
                </StackLayout>
                <PermissionWrapper :permission="EPartnersPerm.PARTNERS_VIEW">
                    <PartnersSelector
                        :partnerId="data.partner_id"
                        :partner_config="data.partner_config"
                        @update="handleChange"
                        class="w-full" />
                </PermissionWrapper>
            </StackLayout>
            <StackLayout direction="row" :gap="8" class="w-full">
                <DateTimePickerField
                    :label="$t('event.model.startAt')"
                    :modelValue="data?.start_at ? formatDate?.(data.start_at) : undefined"
                    @change="handleChangeStartDate(moment($event).utc().toDate())"
                    :hasError="hasErrors.startDateError"
                    :minDatetime="moment().startOf('d').utc().toDate()"
                    required
                    class="w-full" />
                <DateTimePickerField
                    :label="$t('event.model.endAt')"
                    :modelValue="data?.end_at ? formatDate?.(data.end_at) : undefined"
                    @change="handleChange({ end_at: moment($event).utc().toDate() })"
                    :minDatetime="moment(data.start_at).utc().toDate()"
                    :hasError="hasErrors.endDateError"
                    required
                    class="w-full" />
            </StackLayout>
            <TimezoneSelector :data="data.tz" @change="handleChange($event)" />
        </StackLayout>
    </InformationBaseCard>
</template>
